// src/pages/OffersPage.js
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { useLocks } from '../hooks/useLocks';
import { useAuth } from '../context/AuthContext';
import BottomNav from '../components/BottomNav';

export default function OffersPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { lockCoupon, getLockStatus } = useLocks(user);

  const [stores, setStores] = useState([]);
  const [couponsMap, setCouponsMap] = useState({}); // storeId → coupons[]
  const [loading, setLoading] = useState(true);
  const [locking, setLocking] = useState(null);

  useEffect(() => {
    api.getMalls()
      .then(async (malls) => {
        if (!malls.length) return;

        // Load stores from all malls. These responses already include active coupons.
        const allStoresArr = await Promise.all(malls.map(m => api.getStores(m.id)));
        const allStores = allStoresArr.flat();

        const storesWithDeals = allStores.filter(s => (s.coupons?.length || 0) > 0);
        setStores(storesWithDeals);
        setCouponsMap(Object.fromEntries(storesWithDeals.map(s => [s.id, s.coupons || []])));
      })
      .catch((err) => {
        console.error('Offers load error:', err.message);
        setStores([]);
        setCouponsMap({});
      })
      .finally(() => setLoading(false));
  }, []);

  const handleLock = async (e, couponId) => {
    e.stopPropagation();
    if (!user) { navigate('/auth?redirect=/offers'); return; }
    const status = getLockStatus(couponId);
    if (status.status === 'locked' || status.status === 'cooldown') return;
    setLocking(couponId);
    try {
      await lockCoupon(couponId);
    } catch (err) {
      alert(err.message);
    } finally {
      setLocking(null);
    }
  };

  const renderLockBtn = (couponId) => {
    const status = getLockStatus(couponId);
    const isLocking = locking === couponId;
    if (isLocking) return <button className="offer-lock-btn" disabled>…</button>;
    if (status.status === 'locked') return (
      <div className="offer-lock-wrap">
        <button className="offer-lock-btn locked" disabled>✓ Locked</button>
        <span className="offer-countdown">{status.countdown}</span>
      </div>
    );
    if (status.status === 'cooldown') return (
      <button className="offer-lock-btn cooldown" disabled>🕐 {status.hoursLeft}h</button>
    );
    return (
      <button className="offer-lock-btn" onClick={(e) => handleLock(e, couponId)}>
        🔒 Lock
      </button>
    );
  };

  const expiryLabel = (validUntil) => {
    const ms = new Date(validUntil) - new Date();
    const h = Math.floor(ms / 3600000);
    if (h < 1) return '< 1h left';
    if (h < 24) return `${h}h left`;
    return `${Math.floor(h / 24)}d left`;
  };

  const isUrgent = (validUntil) => {
    const h = (new Date(validUntil) - new Date()) / 3600000;
    return h < 24;
  };

  return (
    <div className="offers-root">
      <header className="offers-header">
        <button className="back-icon-btn" onClick={() => navigate('/')}>←</button>
        <div>
          <h1 className="offers-title">⏱ Limited Time Offers</h1>
          <p className="offers-sub">{stores.length} store{stores.length !== 1 ? 's' : ''} with active deals</p>
        </div>
      </header>

      <div className="offers-body">
        {loading ? (
          <div className="offers-skeleton">
            {[1,2,3].map(i => <div key={i} className="offer-store-skeleton" />)}
          </div>
        ) : stores.length === 0 ? (
          <div className="offers-empty">
            <p>No active offers right now.</p>
            <p>Check back soon!</p>
          </div>
        ) : (
          stores.map(store => {
            const coupons = couponsMap[store.id] || [];
            return (
              <div key={store.id} className="offer-store-block">
                {/* Store header */}
                <div className="offer-store-header" onClick={() => navigate(`/store/${store.id}`)}>
                  <div className="offer-store-img" style={{ backgroundImage: store.imageUrl ? `url(${store.imageUrl})` : undefined }}>
                    {!store.imageUrl && <span>🏪</span>}
                  </div>
                  <div className="offer-store-info">
                    <p className="offer-store-name">{store.name}</p>
                    <p className="offer-store-meta">{store.category} · Floor {store.floorNumber ?? '–'}</p>
                  </div>
                  <span className="offer-store-arrow">›</span>
                </div>

                {/* Coupons for this store */}
                <div className="offer-coupons">
                  {coupons.map(coupon => {
                    const discount = coupon.discountType === 'PERCENTAGE'
                      ? `${coupon.discountValue}% OFF`
                      : `₹${coupon.discountValue} OFF`;
                    const urgent = isUrgent(coupon.validUntil);
                    return (
                      <div key={coupon.id} className={`offer-coupon-row ${urgent ? 'urgent' : ''}`}>
                        <div className="offer-coupon-left">
                          <span className="offer-coupon-discount">{discount}</span>
                          <div className="offer-coupon-info">
                            <p className="offer-coupon-title">{coupon.title}</p>
                            {coupon.description && <p className="offer-coupon-desc">{coupon.description}</p>}
                          </div>
                        </div>
                        <div className="offer-coupon-right">
                          <span className={`offer-expiry ${urgent ? 'urgent' : ''}`}>
                            {urgent ? '⚡ ' : ''}{expiryLabel(coupon.validUntil)}
                          </span>
                          {renderLockBtn(coupon.id)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })
        )}
        <div className="home-bottom-pad" />
      </div>

      <BottomNav active="offers" />
    </div>
  );
}
